import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components';
import type {
  HistoryDateFilter,
  HistoryDateFilterItem,
  HistoryPageFilter,
} from '@/shared/types';
import { tv } from 'tailwind-variants';
import CalendarIcon from '@/shared/assets/calendar-16-icon.svg?react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from 'lucide-react';
import Close16Icon from '@/shared/assets/close-16-icon.svg?react';
import {
  buildPeriodSelectOptions,
  useDateLocale,
  useDateModal,
} from '@/shared/lib';
import { useCallback, useMemo, useState } from 'react';
import { formatPeriodLabel } from '@/shared/utils';
import {
  buttonSmallClassName,
  buttonSmallIconClassName,
} from '@/shared/components';

type Props = {
  filters: HistoryPageFilter;
  onFiltersChange: (filters: HistoryPageFilter) => void;
  options: HistoryDateFilterItem[];
  onResetPeriod: () => void;
};

const classes = tv({
  slots: {
    wrapper: 'flex items-center gap-[0.4rem] text-primary',
    clearButton:
      'size-[1.6rem] -mr-[0.4rem] rounded-full flex items-center justify-center text-secondary hover:text-primary cursor-pointer',
    clearButtonIcon: 'size-[1.6rem]',
    selectTrigger: [
      buttonSmallClassName,
      'w-auto justify-start whitespace-nowrap focus-visible:ring-0',
    ],
    selectContent:
      'w-auto min-w-0 max-w-none rounded-[1.2rem] border-stroke bg-background/[0.94] backdrop-blur-[0.4rem] shadow-dropdown!',
    selectContentViewport: 'h-auto min-w-0 max-w-none p-[0.4rem]',
    selectItem:
      'w-full *:[span]:last:gap-[0.8rem] rounded-[0.8rem] py-[0.8rem] pl-[1.6rem] pr-[1.6rem] medium5 text-primary cursor-pointer focus:bg-secondary-bg focus:text-primary',
    selectItemCustom: 'pr-[0.8rem]',
    selectItemTitle: 'grow',
    chevronRightIcon: 'size-[1.6rem] text-secondary',
  },
});

const HistoryDateFilterSelect = ({
  filters,
  onFiltersChange,
  options,
  onResetPeriod,
}: Props) => {
  const { t } = useTranslation();
  const c = classes();
  const { dateLocale } = useDateLocale();
  const { openDateModal, closeDialog } = useDateModal();
  const [open, setOpen] = useState(false);

  const { datefilter, datefrom, dateto } = filters;

  const periodOptions = useMemo(
    () => buildPeriodSelectOptions(options),
    [options]
  );

  const updateDateFilter = useCallback(
    (value: HistoryDateFilter | undefined) =>
      onFiltersChange({ ...filters, datefilter: value }),
    [filters, onFiltersChange]
  );

  const updateCustomPeriod = useCallback(
    (dateFrom?: Date, dateTo?: Date) =>
      onFiltersChange({
        ...filters,
        datefilter: 'custom',
        datefrom: dateFrom,
        dateto: dateTo,
      }),
    [filters, onFiltersChange]
  );

  const openRangeModal = useCallback(() => {
    setOpen(false);
    openDateModal({
      onApply: (dateFrom?: Date, dateTo?: Date) => {
        updateCustomPeriod(dateFrom, dateTo);
        closeDialog();
      },
      onBack: closeDialog,
    });
  }, [openDateModal, updateCustomPeriod, closeDialog]);

  const handlePeriodSelect = useCallback(
    (value: HistoryDateFilter) => updateDateFilter(value),
    [updateDateFilter]
  );

  const handleClearDateFilter = useCallback(
    (e: React.PointerEvent<HTMLSpanElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onResetPeriod();
    },
    [onResetPeriod]
  );

  return (
    <Select
      value={datefilter ?? ''}
      onValueChange={handlePeriodSelect}
      open={open}
      onOpenChange={setOpen}>
      <SelectTrigger
        size="sm"
        className={c.selectTrigger()}
        withChevron={false}>
        <div className={c.wrapper()}>
          <CalendarIcon className={buttonSmallIconClassName} />

          {datefilter === 'custom' ? (
            formatPeriodLabel({
              locale: dateLocale,
              dateFrom: datefrom,
              dateTo: dateto,
              defaultLabel: t('historyPage.dateFilterPlaceholder'),
            })
          ) : datefilter ? (
            <SelectValue />
          ) : (
            t('historyPage.dateFilterPlaceholder')
          )}

          {datefilter && (
            <span
              role="button"
              aria-label={t('historyPage.emptyStateViewWithPeriod.buttonText')}
              onPointerDown={handleClearDateFilter}
              className={c.clearButton()}>
              <Close16Icon className={c.clearButtonIcon()} />
            </span>
          )}
        </div>
      </SelectTrigger>

      <SelectContent
        align="start"
        sideOffset={8}
        className={c.selectContent()}
        classNameViewport={c.selectContentViewport()}>
        {periodOptions.map(({ value, name }) => (
          <SelectItem
            key={value}
            value={value ?? ''}
            className={c.selectItem({
              className: value === 'custom' ? c.selectItemCustom() : undefined,
            })}
            showOptionCheckmark={false}>
            <span className={c.selectItemTitle()}>
              {value === 'custom' ? t('historyPage.customPeriod') : name}
            </span>
            {value === 'custom' && (
              <ChevronRightIcon className={c.chevronRightIcon()} />
            )}
          </SelectItem>
        ))}

        <SelectItem
          key="custom"
          value="custom"
          className={c.selectItem({ className: c.selectItemCustom() })}
          showOptionCheckmark={false}
          onPointerDown={openRangeModal}>
          <span className={c.selectItemTitle()}>
            {t('historyPage.customPeriod')}
          </span>
          <ChevronRightIcon className={c.chevronRightIcon()} />
        </SelectItem>
      </SelectContent>
    </Select>
  );
};

export { HistoryDateFilterSelect };
